import AnimationClip from '../AnimationClip';
import { Tween, Easing } from '@tweenjs/tween.js';
import GameObject from '../../../GameObject/GameObject';
import ElementComponent from '../../../Component/UI/Element';
import Renderable from '../../../Component/Renderable';
import Armature from '../../../Prototype/Armature';
import Point from '../../../Utility/Point';
import Scene from '../../../Scene/Scene';
import { StartAudio } from '../../Audio/AudioHelper';
import { AUDIO_UI_STAR_ACHIEVED } from '../../Audio';
import { AudioTrackType } from '../../Audio/AudioClip';
import { DragonbonesAnimationEvent } from '../../../Component/Dragonbones';

export class FillBarScore extends AnimationClip {
    public *execute(
        gameObject: GameObject,
        meta?: any
    ): IterableIterator<number> {
        const fill: GameObject = gameObject.find('fill');
        const glow: GameObject = gameObject.find('glow');
        const star1: GameObject = gameObject.find('star1');
        const star2: GameObject = gameObject.find('star2');
        const star3: GameObject = gameObject.find('star3');

        const fillElem: ElementComponent = fill.getComponent(ElementComponent);
        const glowRenderer: Renderable = glow.getComponent(Renderable);
        const star1Elem: Renderable = star1
            .find('bright')
            .getComponent(Renderable);
        const star2Elem: Renderable = star2
            .find('bright')
            .getComponent(Renderable);
        const star3Elem: Renderable = star3
            .find('bright')
            .getComponent(Renderable);

        glowRenderer.alpha = 0;

        const progress = this.getScoreProgress(meta.score, meta.stars);

        const prevProgress: number = fillElem.scale.x;
        const duration: number = (progress - prevProgress) * 4000;

        const progressTween = this.getTween({ progress: prevProgress });
        progressTween
            .to({ progress: progress }, duration)
            .easing(Easing.Linear.None)
            .onUpdate(t => {
                fillElem.scale.x = t.progress;

                // Star 1.
                if (!star1Elem.visible && meta.score >= meta.stars[0]) {
                    this.starAndGlowEffect(
                        gameObject,
                        star1,
                        1,
                        meta.scene,
                        82,
                        15
                    );
                    star1Elem.visible = true;
                }

                // Star 2.
                if (!star2Elem.visible && meta.score >= meta.stars[1]) {
                    this.starAndGlowEffect(
                        gameObject,
                        star2,
                        1,
                        meta.scene,
                        168,
                        15
                    );
                    star2Elem.visible = true;
                }

                // Star 3.
                if (!star3Elem.visible && meta.score >= meta.stars[2]) {
                    this.starAndGlowEffect(
                        gameObject,
                        star3,
                        2,
                        meta.scene,
                        252,
                        15
                    );
                    star3Elem.visible = true;
                }
            });

        yield* this.startTween(progressTween);

        while (true) yield;
    }

    private getScoreProgress(score: number, stars: number[]): number {
        const currentStar = stars.filter(n => n <= score).length;
        // The difference between required star scores sometimes are different.
        if (currentStar < 3) {
            const prevStarValue = stars[currentStar - 1] || 0;
            const currStarValue = stars[currentStar] - prevStarValue;
            const nextStarProgress = (score - prevStarValue) / currStarValue;
            return (1 / 3) * (currentStar + nextStarProgress);
        }
        return 1;
    }

    private starAndGlowEffect(
        gameObject: GameObject,
        star: GameObject,
        scale: number,
        scene: Scene,
        x: number,
        y: number
    ) {
        const fill: GameObject = gameObject.find('fill');
        const glow: GameObject = gameObject.find('glow');

        const fillElem: ElementComponent = fill.getComponent(ElementComponent);
        const glowRenderer: Renderable = glow.getComponent(Renderable);
        const glowElem: ElementComponent = glow.getComponent(ElementComponent);
        const starElem = star.getComponent(ElementComponent);

        glowElem.scale.x = fillElem.scale.x;

        // Dragonbones effect.
        const armature: Armature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'UI_VFX',
            armatureName: 'starBar_VFX',
            animationName: 'animation',
            scale: new Point(scale, scale),
            position: new Point(x, y),
        });
        gameObject.addChild(armature);

        StartAudio(scene, AUDIO_UI_STAR_ACHIEVED, AudioTrackType.SFX);

        armature.on(DragonbonesAnimationEvent.COMPLETE, () => {
            armature.destroy();
        });

        const alphaTween1: Tween = this.getTween(glowRenderer);
        const alphaTween2: Tween = this.getTween(glowRenderer);
        alphaTween1.to({ alpha: 1 }, 200);
        alphaTween2.to({ alpha: 0 }, 500);
        alphaTween1.chain(alphaTween2);
        alphaTween1.start(this.elapsedTime);
    }
}
